import dayjs from 'dayjs';
import { monthShortLabel } from '../incomeStatement/incomeStatement';
import type { LiquidityForecast, LiquidityItem, LiquidityPeriod } from '../managementDashboard/liquidityApi';

// Display folding of the „Płynność” tab. Money comes from the backend
// (liquidityReportService.js): periods, balances, the bridge and the overdue
// row are taken as they are; this file only names them, orders the table rows
// and scales the bars. Nothing here adds or subtracts money.

export type LiquidityMode = 'weeks' | 'months';

export type LiquidityRowKind = 'overdue' | 'undated' | 'bridge' | 'period';

export interface LiquidityRow {
  key: string;
  kind: LiquidityRowKind;
  start: string;
  end: string | null;
  openingBalance: number | null;
  inflows: number;
  outflows: number;
  fixedCosts: number;
  closingBalance: number | null;
  items: LiquidityItem[];
}

const shortDay = (date: string) => dayjs(date).format('DD.MM');

/** „13–19.10”, „27.10–02.11” for a week; „paź 26” (the first month: „paź 26 · od 30.10”). */
export function periodLabel(row: Pick<LiquidityRow, 'start' | 'end'>, mode: LiquidityMode, index: number, language: string): string {
  if (mode === 'months') {
    const month = monthShortLabel(row.start.slice(0, 7), language);
    return index === 0 && !row.start.endsWith('-01') ? `${month} · od ${shortDay(row.start)}` : month;
  }
  if (!row.end) return shortDay(row.start);
  const sameMonth = row.start.slice(0, 7) === row.end.slice(0, 7);
  return sameMonth ? `${dayjs(row.start).format('DD')}–${shortDay(row.end)}` : `${shortDay(row.start)}–${shortDay(row.end)}`;
}

function periodRow(period: LiquidityPeriod, index: number): LiquidityRow {
  return { key: `p-${index}`, kind: 'period', ...period };
}

/**
 * Table rows of one mode: the overdue receivables and the documents without a
 * due date (both outside every balance) and
 * the movements from the balance day to yesterday come first when they hold
 * anything (the bridge opens with the typed balance and closes with the
 * opening balance of today); then the 13 weeks or 12 months.
 */
export function liquidityRows(forecast: LiquidityForecast, mode: LiquidityMode, balanceAmount: number | null): LiquidityRow[] {
  const rows: LiquidityRow[] = [];
  if (forecast.overdue.count > 0) {
    rows.push({
      key: 'overdue', kind: 'overdue', start: '', end: null, openingBalance: null, inflows: forecast.overdue.amount,
      outflows: 0, fixedCosts: 0, closingBalance: null, items: forecast.overdue.items,
    });
  }
  if (forecast.undated.count > 0) {
    rows.push({
      key: 'undated', kind: 'undated', start: '', end: null, openingBalance: null, inflows: forecast.undated.inflows,
      outflows: forecast.undated.outflows, fixedCosts: 0, closingBalance: null, items: forecast.undated.items,
    });
  }
  const bridge = forecast.bridge;
  if (bridge && bridge.to && bridge.items.length > 0) {
    rows.push({
      key: 'bridge', kind: 'bridge', start: bridge.from, end: bridge.to,
      openingBalance: balanceAmount,
      inflows: bridge.inflows, outflows: bridge.outflows, fixedCosts: bridge.fixedCosts,
      closingBalance: forecast.openingBalance, items: bridge.items,
    });
  }
  const periods = mode === 'weeks' ? forecast.weeks : forecast.months;
  return [...rows, ...periods.map(periodRow)];
}

export interface LiquidityBar {
  key: string;
  value: number;
  /** 0–100 of the larger side (above or below zero). */
  height: number;
  negative: boolean;
}

/** Bars of the closing balance per period, or of the net flow without a balance. */
export function liquidityBars(periods: LiquidityPeriod[]): { bars: LiquidityBar[]; positiveShare: number; showsBalance: boolean } {
  const showsBalance = periods.length > 0 && periods.every((period) => period.closingBalance !== null);
  const values = periods.map((period) => (showsBalance ? period.closingBalance as number : period.net));
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  return {
    showsBalance,
    // Share of the drawing height above the zero line.
    positiveShare: max === 0 && min === 0 ? 1 : max / span,
    bars: values.map((value, index) => ({
      key: `p-${index}`,
      value,
      height: value >= 0 ? (max > 0 ? (100 * value) / max : 0) : (min < 0 ? (100 * value) / min : 0),
      negative: value < 0,
    })),
  };
}

/** Which invoice screens the reader may open (the invoice routes need the invoice allowlists). */
export interface InvoiceAccess {
  sales: boolean;
  purchase: boolean;
}

/**
 * Where an item opens: the invoice, else its ZK / ZD for a reader without the
 * invoice screens, else nothing (`null`: the number without a link); cost
 * invoices and fixed costs open „Koszty i rentowność”.
 */
export function itemHref(item: LiquidityItem, access: InvoiceAccess): string | null {
  if (item.kind === 'SALES_INVOICE') {
    if (access.sales) return `/app/sales-invoices/${item.id}`;
    return item.orderId ? `/app/orders/${item.orderId}` : null;
  }
  if (item.kind === 'PURCHASE_INVOICE') {
    if (access.purchase) return `/app/purchase-invoices/${item.id}`;
    return item.supplierOrderId ? `/app/supplier-orders/${item.supplierOrderId}` : null;
  }
  if (item.kind === 'FIXED_COST' && item.month) return `/app/profitability?tab=costs&month=${item.month}`;
  return '/app/profitability?tab=costs';
}

/** Sum of the items of one flow — the detail list's „Razem”, which must equal its row. */
export function itemsTotal(items: LiquidityItem[], flow: 'IN' | 'OUT'): number {
  const cents = items.filter((item) => item.flow === flow).reduce((sum, item) => sum + Math.round(item.amount * 100), 0);
  return cents / 100;
}
